// =============================================================================
// PTEL - Firebase Configuration
// =============================================================================

import type { FirebaseApp } from 'firebase/app';
import { initializeApp } from 'firebase/app';
import type { Auth} from 'firebase/auth';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import type { Firestore} from 'firebase/firestore';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';

// -----------------------------------------------------------------------------
// Firebase Configuration from Environment Variables
// -----------------------------------------------------------------------------

interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
}

const firebaseConfig: FirebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || '',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
};

// -----------------------------------------------------------------------------
// Validation
// -----------------------------------------------------------------------------

const validateConfig = (): boolean => {
  const requiredKeys: (keyof FirebaseConfig)[] = [
    'apiKey',
    'authDomain',
    'projectId',
  ];

  const missingKeys = requiredKeys.filter((key) => !firebaseConfig[key]);

  if (missingKeys.length > 0 && import.meta.env.DEV) {
    console.warn(
      `⚠️ Firebase config incompleto: ${missingKeys.join(', ')}\n` +
      'Crea .env.local con las credenciales de Firebase Console.'
    );
    return false;
  }
  return true;
};

// -----------------------------------------------------------------------------
// Initialize Firebase (lazy)
// -----------------------------------------------------------------------------

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let db: Firestore | null = null;

const isConfigured = validateConfig();

if (isConfigured && firebaseConfig.apiKey) {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  auth.languageCode = 'es';
  db = getFirestore(app);

  // Emulators (Development local + CI E2E con build de producción)
  // ⚠️ NUNCA activar VITE_USE_FIREBASE_EMULATORS en el build real de producción
  //    que sirve pteltool.es. Los secrets de Cloudflare Pages no la definen,
  //    por lo que la flag es opt-in y queda inactiva por defecto. Solo debe
  //    estar en true en dev local (.env.local) y en el step «Build producción»
  //    del job e2e-tests del workflow CI.
  const USE_EMULATORS = import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true';

  // Guarda defensiva (TD-011.4 M0.4): si alguien define la flag por accidente
  // en Cloudflare Pages y el bundle real alcanza pteltool.es, evitar conectar
  // al emulador y dejar registro visible en consola.
  const isRealProduction =
    typeof window !== 'undefined' &&
    window.location?.hostname === 'pteltool.es';

  if (USE_EMULATORS && isRealProduction) {
    console.error(
      '[PTEL Firebase] VITE_USE_FIREBASE_EMULATORS=true en pteltool.es — ' +
      'flag ignorada por seguridad. Revisa la configuración de Cloudflare Pages.',
    );
  } else if (USE_EMULATORS) {
    console.info('🔧 Usando Firebase Emulators');
    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
    connectFirestoreEmulator(db, 'localhost', 8080);
  }

  // App Check — reCAPTCHA v3 (solo si la clave está configurada)
  const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined;
  if (recaptchaSiteKey) {
    // Token de debug automático en desarrollo local (no bloquea la app)
    if (import.meta.env.DEV) {
      (self as unknown as Record<string, unknown>).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    }
    initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(recaptchaSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
  }
}

// -----------------------------------------------------------------------------
// Collection Names
// -----------------------------------------------------------------------------

export const COLLECTIONS = {
  USERS: 'users',
  MUNICIPALITIES: 'municipalities',
  DOCUMENTS: 'documents',
  INFRASTRUCTURES: 'infrastructures',
  PATTERNS: 'patterns',
  AUDIT_LOG: 'auditLog',
  EDITOR_REQUESTS: 'editorRequests',
} as const;

// -----------------------------------------------------------------------------
// Export
// -----------------------------------------------------------------------------

export { app, auth, db, isConfigured };
export type { FirebaseConfig };
